import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, ArrowLeft, Check, Sparkles, FolderTree, Boxes, Cpu, TrendingUp } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LeadDialog from "@/components/LeadDialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { runPlanner } from "@/lib/api";
import { Reveal } from "@/lib/motion";
import { toast } from "sonner";

const GOALS = ["generate leads", "sell products", "reservations", "appointments", "memberships", "subscriptions", "content publishing", "directory", "marketplace", "community", "portfolio", "education", "nonprofit"];
const AUDIENCE = ["consumer", "B2B", "local", "national", "international"];
const FEATURES = ["blog", "directory", "events", "calendar", "booking", "e-commerce", "marketplace", "membership", "customer portal", "AI assistant", "multilingual", "text-to-speech", "payments", "reviews", "maps", "messaging", "photo galleries", "video", "analytics", "CRM", "forms", "newsletter"];

const inputCls = "bg-white border-slate-200 rounded-lg h-12 focus-visible:ring-brand";

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm rounded-full border transition-colors ${active ? "border-brand bg-brand text-white" : "border-slate-200 bg-white text-slate-600 hover:border-brand hover:text-brand"}`}
    >
      {children}
    </button>
  );
}

export default function Planner() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    company_name: "", industry: "", description: "", location: "", markets: "",
    goals: [], audience: [], features: [], let_ai_recommend: false,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [leadOpen, setLeadOpen] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const generate = async () => {
    if (!form.company_name.trim()) { toast.error("Please enter your company name."); setStep(0); return; }
    setLoading(true);
    try {
      const res = await runPlanner(form);
      setResult(res.blueprint);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("The AI planner couldn't generate a blueprint right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const steps = ["Business", "Goals", "Audience", "Features"];

  return (
    <>
      <Navbar />
      <main className="pt-32 pb-24 min-h-screen bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {!result ? (
            <>
              <Reveal>
                <span className="overline">AI Website Planner</span>
                <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mt-3">
                  Let's design your website blueprint
                </h1>
                <p className="text-slate-500 text-lg mt-4">
                  Answer a few questions and WebsiteTech AI will recommend a structure, features,
                  technology and growth opportunities.
                </p>
              </Reveal>

              <div className="flex items-center gap-2 mt-10 mb-8">
                {steps.map((s, i) => (
                  <div key={s} className="flex items-center gap-2 flex-1">
                    <div className={`flex items-center gap-2 ${i <= step ? "text-slate-900" : "text-slate-400"}`}>
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border ${i < step ? "bg-brand border-brand text-white" : i === step ? "border-brand text-brand" : "border-slate-300"}`}>
                        {i < step ? <Check size={13} /> : i + 1}
                      </span>
                      <span className="text-sm font-medium hidden sm:inline">{s}</span>
                    </div>
                    {i < steps.length - 1 && <div className={`h-0.5 flex-1 rounded ${i < step ? "bg-brand" : "bg-slate-200"}`} />}
                  </div>
                ))}
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6 md:p-10 min-h-[320px]">
                {step === 0 && (
                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                    <Input data-testid="planner-company" placeholder="Company name*" value={form.company_name} onChange={set("company_name")} className={`${inputCls} text-lg`} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input placeholder="Industry" value={form.industry} onChange={set("industry")} className={inputCls} />
                      <Input placeholder="Location" value={form.location} onChange={set("location")} className={inputCls} />
                    </div>
                    <Input placeholder="Markets served (e.g. US, Europe, global)" value={form.markets} onChange={set("markets")} className={inputCls} />
                    <Textarea placeholder="Describe your business in a sentence or two" value={form.description} onChange={set("description")} className="bg-white border-slate-200 rounded-lg min-h-[100px] focus-visible:ring-brand" />
                  </motion.div>
                )}
                {step === 1 && (
                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                    <p className="text-slate-600 mb-5 font-medium">What are your primary goals?</p>
                    <div className="flex flex-wrap gap-2">
                      {GOALS.map((g) => <Chip key={g} active={form.goals.includes(g)} onClick={() => toggle("goals", g)}>{g}</Chip>)}
                    </div>
                  </motion.div>
                )}
                {step === 2 && (
                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                    <p className="text-slate-600 mb-5 font-medium">Who is your audience?</p>
                    <div className="flex flex-wrap gap-2">
                      {AUDIENCE.map((a) => <Chip key={a} active={form.audience.includes(a)} onClick={() => toggle("audience", a)}>{a}</Chip>)}
                    </div>
                  </motion.div>
                )}
                {step === 3 && (
                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                    <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                      <p className="text-slate-600 font-medium">Which features interest you?</p>
                      <Chip active={form.let_ai_recommend} onClick={() => setForm((f) => ({ ...f, let_ai_recommend: !f.let_ai_recommend }))}>
                        <span className="flex items-center gap-1.5"><Sparkles size={13} /> Not sure — let AI recommend</span>
                      </Chip>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {FEATURES.map((f) => <Chip key={f} active={form.features.includes(f)} onClick={() => toggle("features", f)}>{f}</Chip>)}
                    </div>
                  </motion.div>
                )}

                <div className="flex justify-between mt-10 pt-6 border-t border-slate-200">
                  <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="btn-outline rounded-xl px-5 py-3 flex items-center gap-2 disabled:opacity-30">
                    <ArrowLeft size={15} /> Back
                  </button>
                  {step < 3 ? (
                    <button data-testid="planner-next" onClick={() => setStep((s) => s + 1)} className="btn-primary rounded-xl px-6 py-3 flex items-center gap-2">
                      Next <ArrowRight size={15} />
                    </button>
                  ) : (
                    <button data-testid="planner-generate" onClick={generate} disabled={loading} className="btn-primary rounded-xl px-7 py-3 flex items-center gap-2 disabled:opacity-60">
                      {loading ? <Loader2 className="animate-spin" size={17} /> : <>Generate Blueprint <Sparkles size={15} /></>}
                    </button>
                  )}
                </div>
              </div>
            </>
          ) : (
            <Blueprint data={result} onProposal={() => setLeadOpen(true)} onRestart={() => { setResult(null); setStep(0); }} company={form.company_name} />
          )}
        </div>
      </main>
      <Footer />
      <LeadDialog
        open={leadOpen}
        onOpenChange={setLeadOpen}
        title="Generate Your Proposal"
        subtitle="Tell us where to send your tailored WebsiteTech proposal."
        cta="Request Proposal"
        context={{ company: form.company_name, service: "New Website / Proposal", source: "Website Planner" }}
      />
    </>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-brand" />
        <span className="overline">{title}</span>
      </div>
      {children}
    </div>
  );
}

function Blueprint({ data, onProposal, onRestart, company }) {
  return (
    <Reveal>
      <span className="overline">Recommended Website Blueprint</span>
      <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mt-3">{company}</h1>
      <p className="text-slate-500 text-lg mt-4 max-w-3xl">{data.summary}</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-10">
        <Section icon={FolderTree} title="Site Structure">
          <ul className="space-y-1.5">
            {(data.sitemap || []).map((p, i) => (
              <li key={i}>
                <span className="font-semibold text-slate-800">{p.page}</span>
                {p.children?.length > 0 && (
                  <ul className="ml-5 mt-1 space-y-1 border-l border-slate-200 pl-3">
                    {p.children.map((c, j) => <li key={j} className="text-sm text-slate-500">{c}</li>)}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </Section>
        <Section icon={Boxes} title="Recommended Features">
          <div className="flex flex-wrap gap-2">
            {(data.recommended_features || []).map((f, i) => (
              <span key={i} className="text-xs font-medium border border-slate-200 bg-slate-50 px-3 py-1.5 rounded-full text-slate-700">{f}</span>
            ))}
          </div>
        </Section>
        <Section icon={Cpu} title="Recommended Technology">
          <div className="flex flex-wrap gap-2">
            {(data.recommended_technology || []).map((t, i) => (
              <span key={i} className="text-xs font-medium border border-blue-200 bg-blue-50 px-3 py-1.5 rounded-full text-brand">{t}</span>
            ))}
          </div>
        </Section>
        <Section icon={TrendingUp} title="Growth Opportunities">
          <ul className="space-y-2">
            {(data.growth_opportunities || []).map((g, i) => (
              <li key={i} className="flex gap-2 text-sm text-slate-600"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand shrink-0" /> {g}</li>
            ))}
          </ul>
        </Section>
      </div>

      {data.recommended_modules?.length > 0 && (
        <div className="mt-4">
          <Section icon={Boxes} title="Recommended Modules">
            <div className="flex flex-wrap gap-2">
              {data.recommended_modules.map((m, i) => (
                <span key={i} className="text-xs font-medium border border-slate-200 bg-slate-50 px-3 py-1.5 rounded-full text-slate-700">{m}</span>
              ))}
            </div>
          </Section>
        </div>
      )}

      <div className="mt-8 flex flex-col sm:flex-row items-center gap-4 border-t border-slate-200 pt-8">
        <p className="font-heading text-2xl font-bold text-slate-900 flex-1">Ready to build it?</p>
        <button onClick={onRestart} className="btn-outline rounded-xl px-5 py-3.5">Start Over</button>
        <button data-testid="generate-proposal-btn" onClick={onProposal} className="btn-primary rounded-xl px-7 py-3.5 flex items-center gap-2">
          Generate Proposal <ArrowRight size={16} />
        </button>
      </div>
    </Reveal>
  );
}
